import React, { useMemo } from "react";
import { Box } from "@mui/material";
import FieldOverlay from "./StableFieldOverlay";
import SelectionHandles from "./SelectionHandles";
import { selectWidget } from "./widgetSelection";

const WidgetOverlayRow = ({
  field,
  index,
  eventContextRef,
  mainRect,
  checkboxRect,
  isPrimarySelected,
  isExtraSelected,
  groupSelectionRect,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  currCursor,
  isUnassigned,
  fixedInputs,
  activeEditorTool,
  setCurrentProcessingFile,
  isPreview,
  formField,
  shouldMarkCircle,
  isMultiline,
  isPreviewHovered,
  setOpenEditBlockDialog,
  setSelectedBlock,
  onFavouriteDrop,
  favouriteDropDisabled,
  inputRef,
  childInputRef,
  pdfContainerRef,
  scale,
  pageNumber,
}) => {
  const {
    handleTaggedFieldNavigation,
    handleMouseDown,
    setStartPoint,
    setRect,
    setIsDrawing,
    setCurrCursor,
    setFreeSelectStartPoint,
    setMoving,
    setIsSelecting,
    setInitialMousePosition,
    setInitialInputSize,
    setInputPosition,
    setSelectionRect,
    setResizing,
    setResizeDirection,
    setFocusedField,
    handleFieldChange,
    assignLabelFromSelectedPdfText,
  } = useMemo(() => {
    const names = [
      "handleTaggedFieldNavigation",
      "handleMouseDown",
      "setStartPoint",
      "setRect",
      "setIsDrawing",
      "setCurrCursor",
      "setFreeSelectStartPoint",
      "setMoving",
      "setIsSelecting",
      "setInitialMousePosition",
      "setInitialInputSize",
      "setInputPosition",
      "setSelectionRect",
      "setResizing",
      "setResizeDirection",
      "setFocusedField",
      "handleFieldChange",
      "assignLabelFromSelectedPdfText",
    ];
    return Object.fromEntries(
      names.map((name) => [
        name,
        (...args) => eventContextRef.current[name]?.(...args),
      ]),
    );
  }, [eventContextRef]);
  return (
    <React.Fragment key={field.name ?? index}>
      {isPrimarySelected && selectToolActive && (
        <Box
          ref={inputRef}
          sx={{
            position: "absolute",
            left: `${mainRect.left}px`,
            top: `${mainRect.top}px`,
            width: `${mainRect.width}px`,
            height: `${mainRect.height}px`,
            border: "none",
            outline: "2px dotted blue",
            outlineOffset: "0px",
            boxSizing: "border-box",
            backgroundColor: "transparent",
            zIndex: 100,
          }}
          onMouseDown={(e) => {
            if (isPreview && eventContextRef.current.onPreviewFieldClick) {
              e.stopPropagation();
              eventContextRef.current.onPreviewFieldClick(field, e);
              return;
            }

            if (handleTaggedFieldNavigation(e, field)) return;

            handleMouseDown(
              e,
              eventContextRef.current.currentProcessingFile,
              setCurrentProcessingFile,
              field,
              scale,
              "move",
              "default",
              pdfContainerRef,
              setStartPoint,
              setRect,
              setIsDrawing,
              setCurrCursor,
              setFreeSelectStartPoint,
              setMoving,
              setIsSelecting,
              setInitialMousePosition,
              setInitialInputSize,
              setInputPosition,
              setSelectionRect,
              setResizing,
              setResizeDirection,
              pageNumber,
            );
          }}
          onDoubleClick={() => {
            if (
              eventContextRef.current.currentProcessingFile?.document
                ?.tempFields?.selectedExtraFields?.length > 1
            ) {
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    selectedExtraFields:
                      prevFile.document.tempFields.selectedExtraFields.filter(
                        (f) => f.name !== field.name,
                      ),
                  },
                },
              }));
            }
          }}
        >
          {!groupSelectionRect && (
            <SelectionHandles
              onHandleMouseDown={(e, pos) => {
                e.stopPropagation();
                handleMouseDown(
                  e,
                  eventContextRef.current.currentProcessingFile,
                  setCurrentProcessingFile,
                  field,
                  scale,
                  pos.direction,
                  pos.cursor,
                  pdfContainerRef,
                  setStartPoint,
                  setRect,
                  setIsDrawing,
                  setCurrCursor,
                  setFreeSelectStartPoint,
                  setMoving,
                  setIsSelecting,
                  setInitialMousePosition,
                  setInitialInputSize,
                  setInputPosition,
                  setSelectionRect,
                  setResizing,
                  setResizeDirection,
                  pageNumber,
                );
              }}
            />
          )}
        </Box>
      )}

      {isExtraSelected && (
        <Box
          ref={childInputRef}
          onClick={(e) => {
            if (isPreview && eventContextRef.current.onPreviewFieldClick) {
              e.stopPropagation();
              eventContextRef.current.onPreviewFieldClick(field, e);
              return;
            }

            if (handleTaggedFieldNavigation(e, field)) return;
            if (currCursor === "crosshair") return;
            if (e.ctrlKey || e.metaKey) {
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    selectedExtraFields:
                      prevFile.document.tempFields.selectedExtraFields.filter(
                        (f) => f.name !== field?.name,
                      ),
                  },
                },
              }));
            } else {
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    selectedFormField: field,
                    selectedPdfTextItem: null,
                    selectedExtraPdfTextItems: [],
                  },
                },
              }));
            }
          }}
          sx={{
            position: "absolute",
            left: `${mainRect.left}px`,
            top: `${mainRect.top}px`,
            width: `${mainRect.width}px`,
            height: `${mainRect.height}px`,
            border: "none",
            outline: "2px dotted blue",
            outlineOffset: "0px",
            boxSizing: "border-box",
            backgroundColor: "transparent",
            zIndex: 20,
          }}
        />
      )}

      <FieldOverlay
        field={field}
        rect={mainRect}
        checkboxRect={checkboxRect}
        isDrawing={false}
        isSelected={isPrimarySelected}
        selectToolActive={selectToolActive}
        typingToolActive={typingToolActive}
        highlightFields={highlightFields}
        textOverlayDisplayMode={textOverlayDisplayMode}
        focusedField={focusedField}
        currCursor={currCursor}
        isUnassigned={isUnassigned}
        fixedInputs={fixedInputs}
        activeEditorTool={activeEditorTool}
        setCurrentProcessingFile={setCurrentProcessingFile}
        isPreview={isPreview}
        formField={formField}
        shouldMarkCircle={shouldMarkCircle}
        isMultiline={isMultiline}
        isPreviewHovered={isPreviewHovered}
        setOpenEditBlockDialog={setOpenEditBlockDialog}
        setSelectedBlock={setSelectedBlock}
        onFocus={(e) => {
          if (
            selectToolActive ||
            fixedInputs.some((prefix) => field.name.startsWith(prefix))
          ) {
            e.preventDefault();
            return;
          }
          setFocusedField(field.name);
        }}
        onMouseDown={(e) => {
          if (
            (selectToolActive && field.name.startsWith("date_field")) ||
            fixedInputs.some((prefix) => field.name.startsWith(prefix))
          ) {
            e.preventDefault();
          }
        }}
        onBlur={() => setFocusedField(null)}
        onChange={(e) => {
          if (fixedInputs.some((prefix) => field.name.startsWith(prefix))) {
            return;
          }

          if (field.type === "PDFCheckBox" || field.type === "PDFRadioGroup") {
            if (selectToolActive) return;
            const isChecked = e.target.checked;
            setCurrentProcessingFile((prevFile) => ({
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  formFields: prevFile.document.tempFields.formFields.map(
                    (f) =>
                      f.name === field.name ? { ...f, value: isChecked } : f,
                  ),
                },
              },
            }));
          } else {
            handleFieldChange(e, field);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();

          if (isPreview && eventContextRef.current.onPreviewFieldClick) {
            eventContextRef.current.onPreviewFieldClick(field, e);
            return;
          }

          if (handleTaggedFieldNavigation(e, field)) return;
          if (currCursor === "crosshair") return;

          if (
            (e.ctrlKey || e.metaKey) &&
            eventContextRef.current.currentProcessingFile?.document?.tempFields
              ?.selectedPdfTextItem
          ) {
            setCurrentProcessingFile((prevFile) =>
              assignLabelFromSelectedPdfText(prevFile, field),
            );
            return;
          }

          const additive = e.ctrlKey || e.metaKey;
          setCurrentProcessingFile((prevFile) =>
            selectWidget(prevFile, field, additive),
          );
        }}
        onCheckboxOverlayClick={(e) => {
          e.stopPropagation();

          if (isPreview && eventContextRef.current.onPreviewFieldClick) {
            eventContextRef.current.onPreviewFieldClick(field, e);
            return;
          }

          if (handleTaggedFieldNavigation(e, field)) return;
          if (currCursor === "crosshair") return;

          if (
            (e.ctrlKey || e.metaKey) &&
            eventContextRef.current.currentProcessingFile?.document?.tempFields
              ?.selectedPdfTextItem
          ) {
            setCurrentProcessingFile((prevFile) =>
              assignLabelFromSelectedPdfText(prevFile, field),
            );
            return;
          }

          if (!selectToolActive) {
            setCurrentProcessingFile((prevFile) => ({
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  formFields: prevFile.document.tempFields.formFields.map(
                    (f) =>
                      f.name === field.name
                        ? { ...f, value: !field?.value }
                        : f,
                  ),
                },
              },
            }));
          }

          if (
            eventContextRef.current.currentProcessingFile?.document?.tempFields
              ?.selectedExtraFields?.length > 0 &&
            eventContextRef.current.currentProcessingFile?.document?.tempFields?.selectedExtraFields.some(
              (f) =>
                f.name === field.name &&
                (!eventContextRef.current.currentProcessingFile?.document
                  ?.tempFields?.selectedFormField ||
                  eventContextRef.current.currentProcessingFile?.document
                    ?.tempFields?.selectedFormField?.name !== f.name),
            )
          ) {
            if (e.ctrlKey || e.metaKey) {
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    selectedExtraFields:
                      prevFile.document.tempFields.selectedExtraFields.filter(
                        (f) => f.name !== field?.name,
                      ),
                  },
                },
              }));
            } else {
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    selectedFormField: field,
                    selectedPdfTextItem: null,
                    selectedExtraPdfTextItems: [],
                  },
                },
              }));
            }
          } else {
            const additive = e.ctrlKey || e.metaKey;
            setCurrentProcessingFile((prevFile) =>
              selectWidget(prevFile, field, additive),
            );
          }
        }}
        onFavouriteDrop={onFavouriteDrop}
        favouriteDropDisabled={favouriteDropDisabled}
      />
    </React.Fragment>
  );
};

export default React.memo(WidgetOverlayRow);
